import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import JSZip from 'jszip';
import { getSpineManager } from './spine-manager';

export type ExportFormat = 'gif' | 'webm' | 'png-sequence' | 'png-frame';
export type ExportFps = 12 | 24 | 30;

export interface ExportOptions {
  format: ExportFormat;
  fps: ExportFps;
  duration: number; // seconds
}

/** Yield to browser between heavy frames so UI stays responsive. */
function yieldFrame(): Promise<void> {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

/** Capture offscreen canvas to a PNG Blob. */
function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG capture failed'))), 'image/png');
  });
}

/** Export animation as GIF. Returns a Blob of type 'image/gif'. */
export async function exportAsGif(
  options: ExportOptions,
  onProgress?: (progress: number) => void,
): Promise<Blob> {
  const manager = getSpineManager();
  const { width, height } = manager.getArtboardDimensions();
  const { fps, duration } = options;

  const totalFrames = Math.ceil(duration * fps);
  const delay = Math.round(100 / fps); // GIF delay unit = 10ms, so 100/fps gives 10ms per frame

  const gif = GIFEncoder();

  // Offscreen canvas reused each frame
  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const ctx = offscreen.getContext('2d')!;

  // Save current scene time to restore later
  const currentTime = manager.getCurrentTime(
    [...(manager as unknown as { spineInstances: Map<string, unknown> }).spineInstances.keys()][0] ?? '',
    0,
  );

  for (let i = 0; i < totalFrames; i++) {
    const t = i / fps;
    manager.seekAllLayersToTime(t);
    const ok = manager.captureArtboardRawCanvas(offscreen);

    if (ok) {
      const imageData = ctx.getImageData(0, 0, width, height);
      const palette = quantize(imageData.data, 256);
      const index = applyPalette(imageData.data, palette);
      gif.writeFrame(index, width, height, {
        palette,
        delay,
        repeat: 0, // loop forever
      });
    }

    onProgress?.(i / totalFrames);

    // Yield every 4 frames to keep UI responsive
    if (i % 4 === 3) await yieldFrame();
  }

  // Restore time
  manager.seekAllLayersToTime(currentTime);

  gif.finish();
  return new Blob([gif.bytes().buffer as ArrayBuffer], { type: 'image/gif' });
}

/** Export animation as WebM via MediaRecorder (real-time, no frame-by-frame). */
export async function exportAsWebM(
  options: ExportOptions,
  onProgress?: (progress: number) => void,
): Promise<Blob> {
  const manager = getSpineManager();
  const canvas = manager.getPixiCanvas();
  if (!canvas) throw new Error('PixiJS canvas not available');

  const { fps, duration } = options;

  return new Promise((resolve, reject) => {
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
      ? 'video/webm;codecs=vp8'
      : 'video/webm';

    const stream = canvas.captureStream(fps);
    const recorder = new MediaRecorder(stream, { mimeType });
    const chunks: Blob[] = [];

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onerror = (e) => reject(e);

    recorder.onstop = () => {
      resolve(new Blob(chunks, { type: mimeType }));
      stream.getTracks().forEach((t) => t.stop());
    };

    recorder.start();

    const startTime = performance.now();
    const interval = setInterval(() => {
      const elapsed = (performance.now() - startTime) / 1000;
      onProgress?.(Math.min(elapsed / duration, 1));
      if (elapsed >= duration) {
        clearInterval(interval);
        recorder.stop();
      }
    }, 200);

    // Note: WebM records the live canvas in real-time — the user's active playback is captured
  });
}

/**
 * Export animation as a ZIP of numbered PNG frames.
 * Uses the same frame-stepping loop as GIF export but saves lossless PNGs.
 */
export async function exportAsPngSequence(
  options: ExportOptions,
  onProgress?: (progress: number) => void,
): Promise<Blob> {
  const manager = getSpineManager();
  const { width, height } = manager.getArtboardDimensions();
  const { fps, duration } = options;
  const totalFrames = Math.ceil(duration * fps);

  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;

  const currentTime = manager.getCurrentTime(
    [...(manager as unknown as { spineInstances: Map<string, unknown> }).spineInstances.keys()][0] ?? '',
    0,
  );

  const zip = new JSZip();
  const folder = zip.folder('frames')!;

  for (let i = 0; i < totalFrames; i++) {
    const t = i / fps;
    manager.seekAllLayersToTime(t);
    const ok = manager.captureArtboardRawCanvas(offscreen);

    if (ok) {
      // Must await before next seekAllLayersToTime to avoid canvas data being overwritten
      const blob = await canvasToPngBlob(offscreen);
      const padded = String(i).padStart(4, '0');
      folder.file(`frame_${padded}.png`, blob);
    }

    onProgress?.(i / totalFrames);
    if (i % 4 === 3) await yieldFrame();
  }

  manager.seekAllLayersToTime(currentTime);

  return zip.generateAsync({ type: 'blob' });
}

/**
 * Export the current rendered frame as a single PNG.
 * Captures whatever is currently visible on the PixiJS canvas — no seeking.
 */
export async function exportCurrentFramePng(): Promise<Blob> {
  const manager = getSpineManager();
  const { width, height } = manager.getArtboardDimensions();

  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;

  const ok = manager.captureArtboardRawCanvas(offscreen);
  if (!ok) throw new Error('Frame capture failed — nothing rendered yet');

  return canvasToPngBlob(offscreen);
}

/** Trigger browser download for a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
